import {
  FINDINGS_PAGE_SIZE_DEFAULT, FINDINGS_STATUS_DEFAULT, FindingsQuerySchema, type FindingsQuery,
} from '@ametica/shared';

/**
 * Filtre tabuľky nálezov žijú v URL (`useSearchParams`): refresh ich zachová a
 * zdieľaný odkaz ukáže kolegovi to isté. Jediný zdroj pravdy o tvare je
 * `FindingsQuerySchema` zo shared — tá istá, ktorou backend odmieta nezmysly (400).
 *
 * `parse(serialize(q))` je identita; `serialize` vynecháva predvolené hodnoty,
 * aby odkaz na predvolený pohľad bol holé `/vulnerabilities`.
 */
export const DEFAULT_FILTERS: FindingsQuery = FindingsQuerySchema.parse({});

/** Neplatné hodnoty v URL (ručne prepísaný odkaz) → predvolený pohľad, nie chybová stránka. */
export function parseFilters(params: URLSearchParams): { query: FindingsQuery; invalid: boolean } {
  const raw: Record<string, string | string[]> = {};
  for (const key of new Set(params.keys())) {
    const all = params.getAll(key);
    raw[key] = all.length === 1 ? all[0] : all;
  }
  const r = FindingsQuerySchema.safeParse(raw);
  return r.success ? { query: r.data, invalid: false } : { query: DEFAULT_FILTERS, invalid: true };
}

const sameList = (a: readonly string[] | undefined, b: readonly string[]) =>
  a !== undefined && a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

export function serializeFilters(q: FindingsQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.siteId) p.set('siteId', q.siteId);
  if (q.severity && q.severity.length > 0) p.set('severity', q.severity.join(','));
  if (q.type && q.type.length > 0) p.set('type', q.type.join(','));
  if (!sameList(q.status, FINDINGS_STATUS_DEFAULT)) p.set('status', q.status.join(','));
  if (q.patchAvailable !== undefined) p.set('patchAvailable', String(q.patchAvailable));
  if (q.q) p.set('q', q.q);
  if (q.sort !== DEFAULT_FILTERS.sort) p.set('sort', q.sort);
  if (q.dir !== DEFAULT_FILTERS.dir) p.set('dir', q.dir);
  if (q.page !== 1) p.set('page', String(q.page));
  if (q.pageSize !== FINDINGS_PAGE_SIZE_DEFAULT) p.set('pageSize', String(q.pageSize));
  return p;
}

/** Query string pre API — rovnaký tvar ako URL, takže `/api/findings?…` a `/api/findings.csv?…` dostanú to isté. */
export function toApiQuery(q: FindingsQuery): string {
  const s = serializeFilters(q).toString();
  return s ? `?${s}` : '';
}

/** Je nastavený aspoň jeden filter mimo predvoleného pohľadu (bez radenia a stránkovania)? */
export function hasActiveFilters(q: FindingsQuery): boolean {
  return Boolean(q.siteId || q.severity?.length || q.type?.length || q.patchAvailable !== undefined || q.q)
    || !sameList(q.status, FINDINGS_STATUS_DEFAULT);
}

/** Prepnutie hodnoty v zozname (čipy); prázdny zoznam = bez filtra. */
export function toggleIn<T extends string>(list: readonly T[] | undefined, value: T): T[] | undefined {
  const next = list?.includes(value) ? list.filter((x) => x !== value) : [...(list ?? []), value];
  return next.length ? next : undefined;
}
